import { useEffect, useMemo, useRef, useState } from 'react';
import { uploadFile } from './files';
import { supabase } from './supabase';
import { waTemplateDisplay } from './waTemplates';
import { Send, RefreshCw, ExternalLink, Inbox as InboxIcon, AlertTriangle, MessageCircle, Instagram, Paperclip, X, CheckCircle2, FileText, Copy } from 'lucide-react';
import { InstagramInbox } from './InstagramInbox';
import { CABIFY_STATE_ES, CABIFY_DEAD } from './CabifyBatch';
import { WaUsage } from './WaUsage';
import './Inbox.css';

// Bandeja única de WhatsApp: TODAS las conversaciones (de whatsapp_logs), con tags para
// filtrar (Transferencia / Escalado / Sin responder) y caja de respuesta (texto libre 24h).

interface Order {
  id: number;
  tiendanube_id?: string | null;
  source?: string;
  contact_name?: string;
  contact_phone?: string;
  created_at: string;
  status?: string;
  delivery_type?: string | null;
  payment_status?: string;
  gateway_name?: string;
  total_price?: number;
}

interface WaLog {
  id: number;
  direction: 'incoming' | 'outgoing';
  phone: string;
  contact_name?: string | null;
  message_text?: string | null;
  template_name?: string | null;
  media_url?: string | null;
  media_mime_type?: string | null;
  status?: string;
  is_agent?: boolean;
  is_escalation?: boolean;
  order_id?: string | null;
  created_at: string;
}

// Estado del pedido en el chat (03/11/2026): para contestar "¿y mi pedido?" sin abrir la orden.
const ORDER_STATUS_ES: Record<string, string> = {
  nueva: 'Nueva', falta_insumo: 'Falta insumo', cortado: 'Cortado', empaquetado: 'Empaquetado',
  despachado: 'Despachado', entregado: 'Entregado', stock: 'Stock', cancelada: 'Cancelada', presupuesto: 'Presupuesto',
};
const statusTone = (st?: string) => st === 'entregado' ? '#16a34a' : st === 'cancelada' ? '#dc2626' : st === 'falta_insumo' ? '#d97706' : '#0d9488';

// Etiquetas amigables para los mensajes de plantilla (que se loguean sin texto).
const TPL_LABELS: Record<string, string> = {
  transferencia_datos: '💳 Datos de transferencia enviados',
  transferencia_recordatorio: '⏰ Recordatorio de transferencia',
  order_confirmation_util: '✅ Pedido confirmado',
  order_confirmation_epic_week: '✅ Pedido confirmado',
  order_confirmation_epic_week_v2: '✅ Pedido confirmado (Epic Week)',
  cash_order_confirmation_epic_week_v2: '💵 Confirmación efectivo (Epic Week)',
  pedido_listo_retiro: '📦 Listo para retirar',
  order_dispatched: '🚚 Pedido despachado',
  cash_order_confirmation: '💵 Confirmación (efectivo)',
  cash_order_confirmation_epic_week: '💵 Confirmación (efectivo)',
  cash_shipping_decision: '📦 Retiro o envío?',
  review_request_initial_v4: '⭐ Pedido de reseña',
  review_request_initial: '⭐ Pedido de reseña',
  review_foto_recordatorio: '📸 Recordatorio de foto',
  coupon_delivery: '🎟️ Cupón enviado',
};

// "Plantilla enviada: X" es un placeholder viejo del log (no el mensaje real) → lo ignoramos
// para mostrar la etiqueta linda del template.
function isPlaceholderText(t?: string | null): boolean {
  return /^Plantilla enviada:/i.test((t || '').trim());
}

function msgText(m: WaLog): string {
  if (m.message_text && !isPlaceholderText(m.message_text)) return m.message_text;
  if (m.media_url) return m.media_mime_type?.startsWith('video/') ? '🎥 Video' : m.media_mime_type?.startsWith('image/') ? '🖼️ Imagen' : m.media_mime_type === 'application/pdf' ? '📄 PDF' : '📎 Adjunto';
  if (m.template_name) return TPL_LABELS[m.template_name] || `📄 ${m.template_name}`;
  return m.message_text || '';
}

type FilterKey = 'all' | 'unanswered' | 'escalated' | 'transfer';

function phoneTail(phone?: string | null): string { return (phone || '').replace(/\D/g, '').slice(-10); }

function isTransfer(o?: Order): boolean {
  return !!o && o.source === 'tiendanube' && (o.gateway_name || '').toLowerCase().includes('transferencia');
}

function orderDisplayId(o: Order): string {
  const s = (o.source || '').toLowerCase();
  if (s === 'woocommerce' && o.tiendanube_id) return `BT-${String(o.tiendanube_id).replace(/^(WC-|BT-)/i, '')}`;
  if ((s === 'mercadolibre' || s === 'meli') && o.tiendanube_id) return `ML-${o.tiendanube_id}`;
  if (o.tiendanube_id) return String(o.tiendanube_id);
  return `K-${o.id}`;
}

function fmtTime(s: string): string {
  const d = new Date(s);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function Inbox({ orders, showSnackbar, onOrderClick, hideBilling }: {
  orders: Order[];
  /** Rol staff: sin el insight de costo de WhatsApp. */
  hideBilling?: boolean;
  showSnackbar: (msg: string, type: 'success' | 'error') => void;
  onOrderClick: (id: string) => void;
}) {
  const [logs, setLogs] = useState<WaLog[]>([]);
  // Conversaciones dadas por resueltas a mano (botón ✓): tail -> dismissed_at (ms).
  const [dismissals, setDismissals] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [selectedTail, setSelectedTail] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  // Adjunto de imagen para la respuesta manual
  const [imgFile, setImgFile] = useState<File | null>(null);
  const [imgPreview, setImgPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const lastLogTsRef = useRef<string | null>(null); // cursor del refresco incremental
  const logsRef = useRef<WaLog[]>([]); // para decidir el sonido sin depender del render

  // 🔔 Sonido cuando escribe un cliente que estamos atendiendo NOSOTROS (no el bot):
  // la misma regla del handoff — si un humano respondió en las últimas 8h, el bot
  // está callado y la conversación es nuestra → el mensaje nuevo suena.
  function playPing() {
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new Ctx();
      const mk = (freq: number, t0: number) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine'; o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, ctx.currentTime + t0);
        g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t0 + 0.35);
        o.connect(g); g.connect(ctx.destination);
        o.start(ctx.currentTime + t0); o.stop(ctx.currentTime + t0 + 0.4);
      };
      mk(880, 0); mk(1174.66, 0.12); // D6 sobre A5: "ping" amable
      setTimeout(() => ctx.close(), 800);
    } catch { /* sin audio disponible */ }
  }

  function humanAttended(tail: string): boolean {
    const cutoff = Date.now() - 8 * 3600e3;
    return logsRef.current.some(l =>
      phoneTail(l.phone) === tail &&
      l.direction === 'outgoing' &&
      !l.is_agent && !l.template_name &&
      new Date(l.created_at).getTime() > cutoff,
    );
  }
  const [filter, setFilter] = useState<FilterKey>('all');
  const [channel, setChannel] = useState<'whatsapp' | 'instagram'>('whatsapp');
  const threadRef = useRef<HTMLDivElement>(null);

  // tail -> orden más reciente (para chip de orden + tag transferencia)
  const orderByTail = useMemo(() => {
    const m = new Map<string, Order>();
    for (const o of orders) {
      const t = phoneTail(o.contact_phone);
      if (!t) continue;
      const prev = m.get(t);
      if (!prev || new Date(o.created_at) > new Date(prev.created_at)) m.set(t, o);
    }
    return m;
  }, [orders]);

  // Envíos Cabify del pedido del chat abierto (una entrada por etiqueta, con su seguimiento)
  const [cabifyParcels, setCabifyParcels] = useState<{ parcel_id: string; status: string; label: string; tracking: string | null }[]>([]);
  const selOrderId = selectedTail ? orderByTail.get(selectedTail)?.id : undefined;
  useEffect(() => {
    setCabifyParcels([]);
    if (!selOrderId) return;
    let alive = true;
    supabase.from('cabify_deliveries').select('parcel_id, status, external_id, raw').eq('order_id', selOrderId)
      .not('status', 'in', `(${CABIFY_DEAD.join(',')})`).order('created_at', { ascending: true })
      .then(({ data }) => {
        if (!alive) return;
        setCabifyParcels((data || []).map((d: any) => ({
          parcel_id: d.parcel_id, status: d.status,
          label: (data || []).length > 1 ? ((String(d.external_id || '').match(/\d+\/\d+/) || [''])[0]) : '',
          tracking: d.raw?.shipped?.parcels?.[0]?.tracking_url || d.raw?.tracking_url || null,
        })));
      });
    return () => { alive = false; };
  }, [selOrderId]);

  // El polling trae SOLO lo nuevo desde el último mensaje que ya tenemos. Bajar los 3000
  // logs completos cada 20s son 1,2 MB por vuelta y fue parte de lo que reventó la cuota
  // de egress de Supabase (2026-08-07). La carga completa queda para el montaje.
  // "✓ Resuelta": todo lo anterior a este momento deja de contar como escalado/sin responder.
  // Si el cliente escribe DESPUÉS, la conversación vuelve a contar sola.
  async function dismissConversation(tail: string) {
    const now = new Date().toISOString();
    const { error } = await supabase.from('inbox_dismissals')
      .upsert({ tail, dismissed_at: now }, { onConflict: 'tail' });
    if (error) { showSnackbar('No se pudo marcar: ' + error.message, 'error'); return; }
    setDismissals(prev => new Map(prev).set(tail, Date.now()));
    showSnackbar('Conversación dada por resuelta', 'success');
  }

  async function fetchDismissals() {
    const { data } = await supabase.from('inbox_dismissals').select('tail, dismissed_at');
    setDismissals(new Map((data || []).map((d: any) => [d.tail, new Date(d.dismissed_at).getTime()])));
  }

  async function fetchLogs(opts?: { incremental?: boolean }) {
    const COLS = 'id, direction, phone, contact_name, message_text, template_name, media_url, media_mime_type, status, is_agent, is_escalation, order_id, created_at';
    const since = lastLogTsRef.current;
    const inc = !!opts?.incremental && !!since;
    try {
      const { data, error } = inc
        // gte + dedupe por id: si dos mensajes comparten timestamp exacto, no se pierde ninguno.
        ? await supabase.from('whatsapp_logs').select(COLS).gte('created_at', since!).order('created_at', { ascending: true }).limit(500)
        : await supabase.from('whatsapp_logs').select(COLS).order('created_at', { ascending: false }).limit(3000);
      if (error) throw error;
      const rows = (inc ? (data || []) : (data || []).slice().reverse()) as unknown as WaLog[];
      if (rows.length) {
        const maxTs = rows[rows.length - 1].created_at;
        if (!lastLogTsRef.current || maxTs > lastLogTsRef.current) lastLogTsRef.current = maxTs;
      }
      if (inc) {
        setLogs(prev => {
          const seen = new Set(prev.map(l => l.id));
          const fresh = rows.filter(r => !seen.has(r.id));
          // 🔔 mensaje entrante en conversación atendida por un humano → ping
          if (fresh.some(r => r.direction === 'incoming' && humanAttended(phoneTail(r.phone)))) {
            playPing();
          }
          const next = fresh.length ? [...prev, ...fresh] : prev;
          logsRef.current = next;
          return next;
        });
      } else {
        setLogs(rows);
        logsRef.current = rows;
      }
    } catch (e) {
      console.error('Inbox fetchLogs error:', e);
    }
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
    fetchLogs();
    fetchDismissals();
    const iv = setInterval(() => fetchLogs({ incremental: true }), 20000);
    return () => clearInterval(iv);
  }, []);

  const convByTail = useMemo(() => {
    const m = new Map<string, WaLog[]>();
    for (const l of logs) {
      const t = phoneTail(l.phone);
      if (!t) continue;
      if (!m.has(t)) m.set(t, []);
      m.get(t)!.push(l);
    }
    return m;
  }, [logs]);

  const conversations = useMemo(() => {
    const arr = Array.from(convByTail.entries()).map(([tail, msgs]) => {
      const order = orderByTail.get(tail);
      const visible = msgs.filter(m => !m.is_escalation);
      const last = visible[visible.length - 1] || msgs[msgs.length - 1];
      const name = order?.contact_name || [...msgs].reverse().find(m => m.contact_name)?.contact_name || `+${tail}`;
      // Una reacción (👍/❤️) del cliente NO cuenta como "sin responder": es un acuse, no una consulta.
      const isReaction = (m?: WaLog) => !!m && /reacci[oó]n/i.test(m.message_text || '');
      // "Gracias!" / "ok" / "dale" de cierre no es una consulta pendiente (caso Julia).
      const isShortAck = (m?: WaLog) => !!m && /^(gracias+|muchas gracias|mil gracias|ok+|okey|oka|dale+|listo+|perfecto+|genial|joya|barbaro|bárbaro|buenisimo+|buenísimo|de nada|no,? gracias|👍|🙏|❤️|🧡|😊|🙌)[\s!.,🙏❤️🧡👍😊🙌]*$/iu.test((m.message_text || '').trim());
      const dismissedAt = dismissals.get(tail) || 0;
      // Tampoco cuentan los mensajes VIEJOS (más de 3 días): son backlog, no pendientes accionables
      // (además, pasada la ventana de 24h de WhatsApp ni siquiera se puede responder con texto libre).
      const UNANSWERED_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;
      const recientes = !!last && (Date.now() - new Date(last.created_at).getTime()) < UNANSWERED_MAX_AGE_MS;
      const unanswered = !!last && last.direction === 'incoming' && !isReaction(last) && !isShortAck(last) && recientes
        && new Date(last.created_at).getTime() > dismissedAt;
      const lastEsc = [...msgs].reverse().find(m => m.is_escalation);
      const lastHuman = [...msgs].reverse().find(m => m.direction === 'outgoing' && m.is_agent === false && !m.is_escalation);
      const escalated = !!lastEsc && (!lastHuman || new Date(lastEsc.created_at) > new Date(lastHuman.created_at))
        && new Date(lastEsc.created_at).getTime() > dismissedAt;
      const transfer = isTransfer(order);
      const lastTime = last ? new Date(last.created_at).getTime() : 0;
      return { tail, order, msgs, last, name, unanswered, escalated, transfer, lastTime };
    });
    arr.sort((a, b) => (Number(b.escalated) - Number(a.escalated)) || (b.lastTime - a.lastTime));
    return arr;
  }, [convByTail, orderByTail, dismissals]);

  const counts = useMemo(() => ({
    all: conversations.length,
    unanswered: conversations.filter(c => c.unanswered).length,
    escalated: conversations.filter(c => c.escalated).length,
    transfer: conversations.filter(c => c.transfer).length,
  }), [conversations]);

  const filtered = useMemo(() => conversations.filter(c =>
    filter === 'all' ? true : filter === 'unanswered' ? c.unanswered : filter === 'escalated' ? c.escalated : c.transfer
  ), [conversations, filter]);

  const selected = selectedTail ? conversations.find(c => c.tail === selectedTail) : null;

  useEffect(() => {
    if ((!selectedTail || !conversations.find(c => c.tail === selectedTail)) && filtered.length) {
      setSelectedTail(filtered[0].tail);
    }
  }, [filtered, conversations, selectedTail]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [selected?.msgs.length, selectedTail]);

  // Lee el archivo como base64 puro (sin el prefijo data:...;base64,)
  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(',')[1] || '');
      r.onerror = () => reject(new Error('No se pudo leer el archivo'));
      r.readAsDataURL(file);
    });
  }

  function pickImage(file: File | null | undefined) {
    if (!file) return;
    const esVideo = /^video\//i.test(file.type);
    const esPdf = file.type === 'application/pdf';
    if (!esVideo && !esPdf && !/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      showSnackbar('Se pueden adjuntar imágenes (JPG, PNG, WEBP), videos MP4 o PDF', 'error');
      return;
    }
    // Meta solo acepta MP4/3GP en video, y con límites de peso distintos por tipo.
    if (esVideo && !/^video\/(mp4|3gpp?)$/i.test(file.type)) {
      showSnackbar('El video tiene que ser MP4 (WhatsApp no acepta otros formatos)', 'error');
      return;
    }
    const max = esPdf ? 100 * 1024 * 1024 : esVideo ? 16 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > max) {
      showSnackbar(`El archivo pesa ${(file.size / 1024 / 1024).toFixed(1)} MB y el máximo son ${max / 1024 / 1024} MB`, 'error');
      return;
    }
    setImgFile(file);
    setImgPreview(URL.createObjectURL(file));
  }

  function clearImage() {
    if (imgPreview) URL.revokeObjectURL(imgPreview);
    setImgFile(null); setImgPreview(null);
  }

  async function sendReply() {
    // Con imagen el texto es opcional (va como caption); sin imagen hace falta texto.
    if (!selected || sending || (!reply.trim() && !imgFile)) return;
    setSending(true);
    try {
      const phone = selected.order?.contact_phone || selected.msgs[selected.msgs.length - 1]?.phone || `+${selected.tail}`;
      const common = { phone, contact_name: selected.name, order_id: selected.order ? orderDisplayId(selected.order) : null };
      let data: any, error: any;
      if (imgFile && (/^video\//i.test(imgFile.type) || imgFile.type === 'application/pdf')) {
        // VIDEO y PDF: los subimos a Storage y le pasamos la URL a Meta, que la descarga.
        // En base64 un mp4 o un PDF grande no entran en el body de la edge function.
        const ext = imgFile.type === 'application/pdf' ? 'pdf' : 'mp4';
        const path = `wa-out/${phone.replace(/\D/g, '')}-${Date.now()}.${ext}`;
        const url = await uploadFile('whatsapp-media', path, imgFile, imgFile.type); // R2 (02/11/2026)
        ({ data, error } = await supabase.functions.invoke('whatsapp-send-media', {
          body: { ...common, media_url: url, mime_type: imgFile.type, caption: reply.trim(), filename: imgFile.name },
        }));
      } else if (imgFile) {
        ({ data, error } = await supabase.functions.invoke('whatsapp-send-media', {
          body: { ...common, media_b64: await fileToBase64(imgFile), mime_type: imgFile.type, caption: reply.trim() },
        }));
      } else {
        ({ data, error } = await supabase.functions.invoke('whatsapp-send-text', {
          body: { ...common, body: reply.trim() },
        }));
      }
      if (error) throw error;
      if (!data?.ok) {
        showSnackbar(data?.outside_window
          ? 'Fuera de la ventana de 24h: el cliente tiene que escribirte primero.'
          : `No se pudo enviar: ${data?.error || 'error'}`, 'error');
      } else {
        setReply(''); clearImage(); showSnackbar('Mensaje enviado', 'success'); fetchLogs();
      }
    } catch (e) {
      showSnackbar('Error al enviar el mensaje', 'error');
    }
    setSending(false);
  }

  const FILTERS: { k: FilterKey; label: string }[] = [
    { k: 'all', label: 'Todos' },
    { k: 'unanswered', label: 'Sin responder' },
    { k: 'escalated', label: 'Escalados' },
    { k: 'transfer', label: 'Transferencia' },
  ];

  return (
    <div className="ibx-wrap">
      <div className="ibx-channels">
        <button className={`ibx-channel ${channel === 'whatsapp' ? 'active' : ''}`} onClick={() => setChannel('whatsapp')}><MessageCircle size={15} /> WhatsApp</button>
        <button className={`ibx-channel ${channel === 'instagram' ? 'active' : ''}`} onClick={() => setChannel('instagram')}><Instagram size={15} /> Instagram</button>
      </div>
      {channel === 'whatsapp' && !hideBilling && <WaUsage />}
      {channel === 'instagram' ? <InstagramInbox showSnackbar={showSnackbar} /> : (
    <div className="ibx">
      {/* Lista */}
      <div className="ibx-list">
        <div className="ibx-list-head">
          <span><InboxIcon size={16} /> Atención</span>
          <button className="ibx-refresh" onClick={() => fetchLogs()} title="Actualizar"><RefreshCw size={15} /></button>
        </div>
        <div className="ibx-filters">
          {FILTERS.map(f => (
            <button key={f.k} className={`ibx-fchip ${filter === f.k ? 'active' : ''} ${f.k === 'escalated' && counts.escalated ? 'urgent' : ''}`} onClick={() => setFilter(f.k)}>
              {f.label} <span className="ibx-fcount">{counts[f.k]}</span>
            </button>
          ))}
        </div>
        <div className="ibx-conv-scroll">
          {loading && conversations.length === 0 ? (
            <div className="ibx-empty">Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="ibx-empty">No hay conversaciones{filter !== 'all' ? ' en este filtro' : ''}.</div>
          ) : filtered.map(c => {
            const preview = c.last ? msgText(c.last).slice(0, 42) : '';
            return (
              <button key={c.tail} className={`ibx-conv ${selectedTail === c.tail ? 'active' : ''}`} onClick={() => setSelectedTail(c.tail)}>
                <div className="ibx-conv-top">
                  <span className={`ibx-conv-name ${c.unanswered ? 'bold' : ''}`}>{c.name}</span>
                  {c.last && <span className="ibx-conv-time">{fmtTime(c.last.created_at)}</span>}
                </div>
                <div className="ibx-tags">
                  {c.escalated && <span className="ibx-tag esc">⚠ Escalado</span>}
                  {c.transfer && <span className="ibx-tag trf">Transferencia</span>}
                  {c.unanswered && !c.escalated && <span className="ibx-tag una">Sin responder</span>}
                  {c.order && <span className="ibx-tag ord">#{orderDisplayId(c.order)}{c.order.status ? ` · ${ORDER_STATUS_ES[c.order.status] || c.order.status}` : ''}</span>}
                </div>
                <div className="ibx-conv-preview">{c.last?.direction === 'outgoing' ? 'Vos: ' : ''}{preview}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Hilo + respuesta */}
      <div className="ibx-thread-pane">
        {!selected ? (
          <div className="ibx-empty big">Elegí una conversación</div>
        ) : (
          <>
            <div className="ibx-thread-head">
              <div>
                <strong>{selected.name}</strong>
                <span className="ibx-thread-sub">
                  {selected.order?.contact_phone || `+${selected.tail}`}
                  {selected.order ? ` · Total $${Number(selected.order.total_price || 0).toLocaleString('es-AR')}` : ''}
                </span>
                {selected.order && (
                  <div className="ibx-order-state">
                    <span className="ibx-state-pill" style={{ color: statusTone(selected.order.status), borderColor: statusTone(selected.order.status) }}>
                      #{orderDisplayId(selected.order)} · {ORDER_STATUS_ES[selected.order.status || ''] || selected.order.status}
                      {selected.order.delivery_type === 'retiro' ? ' · retira en el local' : selected.order.delivery_type === 'envio' ? ' · envío' : ''}
                      {selected.order.payment_status && selected.order.payment_status !== 'paid' ? ' · sin pagar' : ''}
                    </span>
                    {cabifyParcels.map(pc => (
                      <span key={pc.parcel_id} className="ibx-cabify">
                        🛵 {pc.label ? <b>{pc.label}</b> : null} {CABIFY_STATE_ES[pc.status] || pc.status}
                        {pc.tracking && <a href={pc.tracking} target="_blank" rel="noopener noreferrer"> seguimiento <ExternalLink size={11} /></a>}
                        <button type="button" className="ibx-copy-id" title={`Copiar el ID de Cabify: ${pc.parcel_id}`}
                          onClick={() => navigator.clipboard?.writeText(pc.parcel_id).then(() => showSnackbar(`ID de Cabify copiado: ${pc.parcel_id}`, 'success'))}>
                          <Copy size={11} /> ID {pc.parcel_id}
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {(selected.escalated || selected.unanswered) && (
                  <button
                    className="ibx-order-link"
                    style={{ borderColor: '#16a34a', color: '#16a34a' }}
                    title="Sacarla de Escalados/Sin responder. Si el cliente vuelve a escribir, reaparece sola."
                    onClick={() => dismissConversation(selected.tail)}
                  >
                    <CheckCircle2 size={13} /> Resuelta
                  </button>
                )}
                {selected.order && (
                  <button className="ibx-order-link" onClick={() => onOrderClick(orderDisplayId(selected.order!))}>
                    Ver orden #{orderDisplayId(selected.order)} <ExternalLink size={13} />
                  </button>
                )}
              </div>
            </div>

            <div className="ibx-thread" ref={threadRef}>
              {selected.msgs.map(m => m.is_escalation ? (
                <div key={m.id} className="ibx-sysnote"><AlertTriangle size={13} /> {m.message_text}</div>
              ) : (
                <div key={m.id} className={`ibx-msg ${m.direction}`}>
                  <div className={`ibx-bubble ${m.direction} ${m.status === 'failed' ? 'failed' : ''} ${m.is_agent ? 'agent' : ''}`}>
                    {m.media_url && (
                      m.media_mime_type?.startsWith('image/') ? (
                        <a href={m.media_url} target="_blank" rel="noreferrer"><img className="ibx-media" src={m.media_url} alt="Adjunto" /></a>
                      ) : m.media_mime_type?.startsWith('video/') ? (
                        <video className="ibx-media" src={m.media_url} controls preload="metadata" playsInline />
                      ) : (
                        <a className="ibx-doc" href={m.media_url} target="_blank" rel="noreferrer">📄 Ver adjunto</a>
                      )
                    )}
                    {m.template_name ? (
                      <>
                        {waTemplateDisplay(m.template_name, m.message_text, m.contact_name)
                          ? <div className="ibx-text" style={{ whiteSpace: 'pre-wrap' }}>{waTemplateDisplay(m.template_name, m.message_text, m.contact_name)}</div>
                          : <div className="ibx-text ibx-tpl">{msgText(m)}</div>}
                        <div style={{ marginTop: 4 }}>
                          <span style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.03em', background: 'rgba(124,58,237,0.12)', color: '#7c3aed', borderRadius: 5, padding: '1px 7px' }}>
                            📄 {(TPL_LABELS[m.template_name] || m.template_name).replace(/^[^\w]*\s*/, '')}
                          </span>
                        </div>
                      </>
                    ) : (m.message_text && !isPlaceholderText(m.message_text))
                      ? <div className="ibx-text">{m.message_text}</div>
                      : null}
                    {m.order_id && <div className="ibx-order-chip">📦 pedido #{m.order_id}</div>}
                    <div className="ibx-time">{m.is_agent ? '🤖 ' : ''}{fmtTime(m.created_at)}{m.status === 'failed' ? ' · falló' : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {imgPreview && (
          <div className="ibx-attach">
            {imgFile?.type === 'application/pdf'
              ? <FileText size={34} style={{ color: '#951F23', flexShrink: 0 }} />
              : /^video\//i.test(imgFile?.type || '')
              ? <video src={imgPreview} muted playsInline style={{ maxHeight: 54, borderRadius: 8 }} />
              : <img src={imgPreview} alt="Adjunto" />}
            <div className="ibx-attach-info">
              <span className="ibx-attach-name">{imgFile?.name}</span>
              <span className="ibx-attach-size">{((imgFile?.size || 0) / 1024).toFixed(0)} KB · el texto va como epígrafe</span>
            </div>
            <button className="ibx-attach-x" onClick={clearImage} title="Quitar adjunto"><X size={15} /></button>
          </div>
        )}

        <div className="ibx-reply">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,video/mp4,video/3gpp,application/pdf"
            style={{ display: 'none' }}
            onChange={e => { pickImage(e.target.files?.[0]); e.target.value = ''; }}
          />
          <button
            className="ibx-clip"
            onClick={() => fileRef.current?.click()}
            disabled={!selected || sending}
            title="Adjuntar imagen (JPG, PNG, WEBP · 5 MB), video MP4 (hasta 16 MB) o PDF"
          >
            <Paperclip size={16} />
          </button>
          <textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
            onPaste={e => {
              // Pegar una imagen del portapapeles (Ctrl+V) la adjunta directamente.
              const img = Array.from(e.clipboardData.files).find(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
              if (img) { e.preventDefault(); pickImage(img); }
            }}
            placeholder={selected ? (imgFile ? 'Epígrafe del adjunto (opcional)…' : 'Escribí una respuesta… (Enter para enviar)') : 'Elegí una conversación para responder'}
            rows={2}
            disabled={!selected}
          />
          <button className="ibx-send" onClick={sendReply} disabled={!selected || sending || (!reply.trim() && !imgFile)}>
            <Send size={16} /> {sending ? '…' : 'Enviar'}
          </button>
        </div>
        <div className="ibx-note">Al responder vos, el agente se pausa 8h en ese chat. Texto libre solo dentro de las 24h del último mensaje del cliente.</div>
      </div>
    </div>
      )}
    </div>
  );
}
